// Tier 2 bounded metadata store for the ESA Panel.
// Uses Zustand + immer middleware for correct Map mutation handling (DR-16).
// Message bodies are NOT stored here — they live in MessageCache (Tier 3, DR-1).
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { enableMapSet } from 'immer';

import type { InboxItem, InboxNotification, ResolvingSession } from '../types';
import type { ESACacheMessage } from '../cache/MessageCache';
import { MAX_ESA_NOTIFICATIONS, MAX_ESA_THREADS } from '../../../constants/timing';

// Enable immer's MapSet plugin once at module load time so Map/Set mutations
// work correctly inside immer-wrapped Zustand producers.
enableMapSet();

export interface ThreadMeta {
  id: string;
  title: string | null;
  lastUpdatedAt: number;
  surfaceOrigin?: string;
  hasActiveHITL: boolean;
  status: 'active' | string;
  /** Short preview of the last message — shown in the thread list item. */
  lastMessagePreview?: string;
}

export interface HITLNotification {
  id: string;
  agentRunId: string;
  agentUkey: string;
  question: string;
  requestType: string | null;
  priority: 'NORMAL' | string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED' | 'EXPIRED';
  conversationId: string;
  createdAt: string;
}

export interface SurfaceSelectionContext {
  solutionId?: string;
  workspaceId?: string;
  canvasId?: string;
  [key: string]: unknown;
}

interface ESADomainState {
  activeThreadId: string | null;
  threadIndex: Map<string, ThreadMeta>;
  pendingHITL: HITLNotification[];
  streamStatus: 'idle' | 'connecting' | 'streaming' | 'error';
  streamError: string | null;
  /** Correlator for the in-flight converse turn (feat-80 status SSE filter). */
  activeConverseClientSeq: string | null;
  lastSurfaceContext: SurfaceSelectionContext | null;
  composerDraft: string;
  pendingOpenRunId: string | null;
  hilSummary: { pending: number; approved: number; rejected: number } | null;

  // ESA Inbox extensions (DR-9)
  inboxItems: InboxItem[];
  inboxLoading: 'idle' | 'loading' | 'success' | 'error';
  resolvingSession: ResolvingSession | null;
  inboxNotifications: InboxNotification[];

  /** True when an SSE notification has arrived and the user hasn't opened the inbox yet. */
  hasUnreadNotification: boolean;

  /** True when a hil.* event arrived and the user hasn't visited the "Agent Requests" tab. */
  unreadRequests: boolean;
  /** True when a non-hil notification arrived and the user hasn't visited the "Notifications" tab. */
  unreadNotifications: boolean;
  /** True when a hil.created event arrived; cleared once the "Agent Requests" list has been reloaded. */
  inboxRequestsStale: boolean;

  /**
   * Message currently pinned in the canvas expand overlay (DR-8).
   * Null means the overlay is closed.  Only one message can be expanded at a time.
   */
  expandedMessage: ESACacheMessage | null;
}

interface ESADomainActions {
  setActiveThread(threadId: string | null): void;
  upsertThread(meta: ThreadMeta): void;
  removeThread(threadId: string): void;
  addPendingHITL(notification: HITLNotification): void;
  clearExpiredHITL(): void;
  setStreamStatus(status: ESADomainState['streamStatus']): void;
  setStreamError(error: string | null): void;
  setActiveConverseClientSeq(clientSeq: string | null): void;
  setSurfaceContext(context: SurfaceSelectionContext | null): void;
  setComposerDraft(draft: string): void;
  setPendingOpenRunId(runId: string | null): void;
  updateHilSummary(summary: ESADomainState['hilSummary']): void;
  reset(): void;

  // ESA Inbox actions (DR-9)
  setInboxItems(items: InboxItem[]): void;
  /** Append new items to the existing inbox list (load-more); dedupes by id. */
  appendInboxItems(items: InboxItem[]): void;
  setInboxLoading(state: ESADomainState['inboxLoading']): void;
  resetInbox(): void;
  startResolvingSession(session: ResolvingSession): void;
  advanceResolvingSession(): void;
  setResolvingIndex(index: number): void;
  endResolvingSession(): void;
  markItemResolved(hilId: string): void;
  /**
   * Merge a fresh InboxItem snapshot (e.g. from getHilItem) into both
   * inboxItems and resolvingSession.items so every cached copy stays
   * up-to-date after a status-poll.
   */
  updateInboxItem(updated: InboxItem): void;
  setInboxNotifications(notifications: InboxNotification[]): void;
  addInboxNotification(notification: InboxNotification): void;
  markNotificationRead(id: string): void;
  markAllNotificationsRead(): void;
  setHasUnreadNotification(value: boolean): void;
  setUnreadRequests(value: boolean): void;
  setUnreadNotifications(value: boolean): void;
  setInboxRequestsStale(value: boolean): void;
  /** Open the canvas message-expand overlay for the given message. */
  setExpandedMessage(message: ESACacheMessage): void;
  /** Close the canvas message-expand overlay. */
  clearExpandedMessage(): void;
}

const DEFAULT_STATE: Omit<ESADomainState, 'composerDraft'> = {
  activeThreadId: null,
  threadIndex: new Map(),
  pendingHITL: [],
  streamStatus: 'idle',
  streamError: null,
  activeConverseClientSeq: null,
  lastSurfaceContext: null,
  pendingOpenRunId: null,
  hilSummary: null,
  inboxItems: [],
  inboxLoading: 'idle',
  resolvingSession: null,
  inboxNotifications: [],
  hasUnreadNotification: false,
  unreadRequests: false,
  unreadNotifications: false,
  inboxRequestsStale: false,
  expandedMessage: null,
};

/** Evicts the thread with the smallest lastUpdatedAt from the Map (in-place, immer draft). */
function evictLRUThread(draft: Map<string, ThreadMeta>): void {
  let oldestKey: string | null = null;
  let oldestTs = Infinity;
  for (const [key, meta] of draft) {
    if (meta.lastUpdatedAt < oldestTs) {
      oldestTs = meta.lastUpdatedAt;
      oldestKey = key;
    }
  }
  if (oldestKey !== null) {
    draft.delete(oldestKey);
  }
}

export const useEsaDomainStore = create<ESADomainState & ESADomainActions>()(
  immer(set => ({
    ...DEFAULT_STATE,
    composerDraft: '',

    setActiveThread: (threadId) =>
      set(draft => {
        draft.activeThreadId = threadId;
      }),

    upsertThread: (meta) =>
      set(draft => {
        const existing = draft.threadIndex.get(meta.id);
        if (!existing && draft.threadIndex.size >= MAX_ESA_THREADS) {
          evictLRUThread(draft.threadIndex);
        }
        // Merge so partial updates (e.g. lastUpdatedAt only) do not wipe title.
        draft.threadIndex.set(meta.id, {
          ...(existing ?? {
            id: meta.id,
            title: null,
            lastUpdatedAt: meta.lastUpdatedAt,
            hasActiveHITL: false,
            status: 'active',
          }),
          ...meta,
          id: meta.id,
          title: meta.title ?? existing?.title ?? null,
        });
      }),

    removeThread: (threadId) =>
      set(draft => {
        draft.threadIndex.delete(threadId);
        if (draft.activeThreadId === threadId) {
          draft.activeThreadId = null;
        }
      }),

    addPendingHITL: (notification) =>
      set(draft => {
        const exists = draft.pendingHITL.some(h => h.id === notification.id);
        if (!exists) {
          draft.pendingHITL.push(notification);
        }
      }),

    clearExpiredHITL: () =>
      set(draft => {
        draft.pendingHITL = draft.pendingHITL.filter(
          h => h.status !== 'EXPIRED' && h.status !== 'CANCELLED'
        );
      }),

    setStreamStatus: (status) =>
      set(draft => {
        draft.streamStatus = status;
        if (status !== 'error') {
          draft.streamError = null;
        }
        // Drop turn correlator when converse is no longer in flight.
        if (status === 'idle' || status === 'error') {
          draft.activeConverseClientSeq = null;
        }
      }),

    setStreamError: (error) =>
      set(draft => {
        draft.streamError = error;
      }),

    setActiveConverseClientSeq: (clientSeq) =>
      set(draft => {
        draft.activeConverseClientSeq = clientSeq;
      }),

    setSurfaceContext: (context) =>
      set(draft => {
        draft.lastSurfaceContext = context;
      }),

    setComposerDraft: (draftText) =>
      set(draft => {
        draft.composerDraft = draftText;
      }),

    setPendingOpenRunId: (runId) =>
      set(draft => {
        draft.pendingOpenRunId = runId;
      }),

    updateHilSummary: (summary) =>
      set(draft => {
        draft.hilSummary = summary;
      }),

    reset: () =>
      set(draft => {
        const kept = draft.composerDraft;
        Object.assign(draft, DEFAULT_STATE);
        draft.threadIndex = new Map();
        draft.composerDraft = kept;
        draft.inboxItems = [];
        draft.inboxLoading = 'idle';
        draft.resolvingSession = null;
        draft.inboxNotifications = [];
      }),

    // ESA Inbox action implementations

    setInboxItems: (items) =>
      set(draft => {
        draft.inboxItems = items;
      }),

    appendInboxItems: (items) =>
      set(draft => {
        const existing = new Set(draft.inboxItems.map((i: InboxItem) => i.id));
        for (const item of items) {
          if (!existing.has(item.id)) {
            draft.inboxItems.push(item);
          }
        }
      }),

    setInboxLoading: (state) =>
      set(draft => {
        draft.inboxLoading = state;
      }),

    resetInbox: () =>
      set(draft => {
        draft.inboxItems = [];
        draft.inboxLoading = 'idle';
      }),

    startResolvingSession: (session) =>
      set(draft => {
        draft.resolvingSession = session;
      }),

    advanceResolvingSession: () =>
      set(draft => {
        if (draft.resolvingSession) {
          const maxIndex = draft.resolvingSession.items.length - 1;
          if (draft.resolvingSession.currentIndex < maxIndex) {
            draft.resolvingSession.currentIndex += 1;
          }
        }
      }),

    setResolvingIndex: (index) =>
      set(draft => {
        if (draft.resolvingSession) {
          draft.resolvingSession.currentIndex = index;
        }
      }),

    endResolvingSession: () =>
      set(draft => {
        draft.resolvingSession = null;
      }),

    markItemResolved: (hilId) =>
      set(draft => {
        const item = draft.inboxItems.find(i => i.id === hilId);
        if (item) {
          item.status = 'resolved';
        }
        if (draft.resolvingSession) {
          const sessionItem = draft.resolvingSession.items.find(i => i.id === hilId);
          if (sessionItem) {
            sessionItem.status = 'resolved';
          }
        }
      }),

    updateInboxItem: (updated) =>
      set(draft => {
        const idx = draft.inboxItems.findIndex(i => i.id === updated.id);
        if (idx !== -1) {
          draft.inboxItems[idx] = updated;
        }
        if (draft.resolvingSession) {
          const sIdx = draft.resolvingSession.items.findIndex(i => i.id === updated.id);
          if (sIdx !== -1) {
            draft.resolvingSession.items[sIdx] = updated;
          }
        }
      }),

    setInboxNotifications: (notifications) =>
      set(draft => {
        draft.inboxNotifications = notifications.slice(0, MAX_ESA_NOTIFICATIONS);
      }),

    addInboxNotification: (notification) =>
      set(draft => {
        if (draft.inboxNotifications.some(n => n.id === notification.id)) {
          return;
        }
        draft.inboxNotifications.push(notification);
        if (draft.inboxNotifications.length > MAX_ESA_NOTIFICATIONS) {
          draft.inboxNotifications.splice(0, draft.inboxNotifications.length - MAX_ESA_NOTIFICATIONS);
        }
      }),

    markNotificationRead: (id) =>
      set(draft => {
        const notif = draft.inboxNotifications.find(n => n.id === id);
        if (notif) {
          notif.read = true;
        }
      }),

    markAllNotificationsRead: () =>
      set(draft => {
        for (const notif of draft.inboxNotifications) {
          notif.read = true;
        }
      }),

    setHasUnreadNotification: (value) =>
      set(draft => {
        draft.hasUnreadNotification = value;
      }),

    setUnreadRequests: (value) =>
      set(draft => {
        draft.unreadRequests = value;
      }),

    setUnreadNotifications: (value) =>
      set(draft => {
        draft.unreadNotifications = value;
      }),

    setInboxRequestsStale: (value) =>
      set(draft => {
        draft.inboxRequestsStale = value;
      }),

    setExpandedMessage: (message) =>
      set(draft => {
        draft.expandedMessage = message;
      }),

    clearExpandedMessage: () =>
      set(draft => {
        draft.expandedMessage = null;
      }),
  }))
);

/**
 * Serializer for Zustand devtools — converts Map fields to plain objects
 * so the Redux DevTools extension can serialize them correctly.
 */
export function mapToJSON(state: ESADomainState): Record<string, unknown> {
  return {
    ...state,
    threadIndex: Object.fromEntries(state.threadIndex),
  };
}
